/**
 * 87C Country Detection Popup
 * ---
 * Detects visitor country via Shopify's /browsing_context_suggestions.json
 * If not France, shows a subtle Eres-style popup offering language/currency switch.
 * Remembers dismissal for 30 days in localStorage.
 *
 * Progressive enhancement:
 * - If EN locale is available, offers "Switch to English"
 * - Otherwise, shows "Shipping worldwide from France" as reassurance
 */
(function () {
  'use strict';

  /* --- Config -------------------------------------------------------------- */
  var STORAGE_KEY = 'c87_country_dismissed';
  var DISMISS_DAYS = 30;
  var POPUP_DELAY_MS = 1200; /* wait before showing, let page settle */

  /* --- Early exit: already dismissed? -------------------------------------- */
  try {
    var stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      var expires = parseInt(stored, 10);
      if (expires && Date.now() < expires) return;
      /* expired — remove and continue */
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    /* localStorage blocked (private browsing etc.) — skip popup entirely */
    return;
  }

  /* --- Country name map (common Shopify ISO codes) ------------------------- */
  var COUNTRY_NAMES_FR = {
    US: 'les \u00c9tats-Unis', GB: 'le Royaume-Uni', DE: "l'Allemagne",
    BE: 'la Belgique', CH: 'la Suisse', IT: "l'Italie", ES: "l'Espagne",
    NL: 'les Pays-Bas', CA: 'le Canada', AU: "l'Australie",
    JP: 'le Japon', KR: 'la Cor\u00e9e du Sud', CN: 'la Chine',
    BR: 'le Br\u00e9sil', AE: 'les \u00c9mirats arabes unis',
    SA: "l'Arabie saoudite", SE: 'la Su\u00e8de', DK: 'le Danemark',
    NO: 'la Norv\u00e8ge', PT: 'le Portugal', AT: "l'Autriche",
    LU: 'le Luxembourg', IE: "l'Irlande", PL: 'la Pologne',
    CZ: 'la Tch\u00e9quie', GR: 'la Gr\u00e8ce', RO: 'la Roumanie',
    HU: 'la Hongrie', FI: 'la Finlande', SG: 'Singapour',
    HK: 'Hong Kong', IN: "l'Inde", MX: 'le Mexique',
    RU: 'la Russie', TR: 'la Turquie', ZA: "l'Afrique du Sud"
  };

  var COUNTRY_NAMES_EN = {
    US: 'the United States', GB: 'the United Kingdom', DE: 'Germany',
    BE: 'Belgium', CH: 'Switzerland', IT: 'Italy', ES: 'Spain',
    NL: 'the Netherlands', CA: 'Canada', AU: 'Australia',
    JP: 'Japan', KR: 'South Korea', CN: 'China',
    BR: 'Brazil', AE: 'the UAE', SA: 'Saudi Arabia',
    SE: 'Sweden', DK: 'Denmark', NO: 'Norway', PT: 'Portugal',
    AT: 'Austria', LU: 'Luxembourg', IE: 'Ireland', PL: 'Poland',
    CZ: 'Czechia', GR: 'Greece', RO: 'Romania', HU: 'Hungary',
    FI: 'Finland', SG: 'Singapore', HK: 'Hong Kong', IN: 'India',
    MX: 'Mexico', RU: 'Russia', TR: 'Turkey', ZA: 'South Africa'
  };

  /* --- Country flag emoji from ISO code ------------------------------------ */
  function flagEmoji(iso) {
    if (!iso || iso.length !== 2) return '\uD83C\uDF10'; /* globe */
    var a = iso.charCodeAt(0) - 65 + 0x1F1E6;
    var b = iso.charCodeAt(1) - 65 + 0x1F1E6;
    return String.fromCodePoint(a) + String.fromCodePoint(b);
  }

  /* --- Detect country via Shopify browsing context ------------------------- */
  function detectCountry(callback) {
    /* First try Shopify.country (set by some themes / geolocation app) */
    if (window.Shopify && window.Shopify.country) {
      callback(window.Shopify.country);
      return;
    }

    /* Fallback: Shopify's browsing context API */
    fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var code = null;
        if (data && data.detected_values && data.detected_values.country) {
          code = data.detected_values.country.handle;
        }
        callback(code ? code.toUpperCase() : null);
      })
      .catch(function () {
        callback(null);
      });
  }

  /* --- Check if EN locale is published ------------------------------------- */
  function hasEnLocale() {
    /* Shopify exposes published locales in the localization form */
    var forms = document.querySelectorAll('form[action*="/localization"]');
    for (var i = 0; i < forms.length; i++) {
      var opts = forms[i].querySelectorAll('input[name="locale_code"], option[value="en"]');
      for (var j = 0; j < opts.length; j++) {
        if (opts[j].value === 'en') return true;
      }
    }
    /* Also check meta / Shopify.locale presence */
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      /* If /en/ root exists it would be in the alternate links */
      var links = document.querySelectorAll('link[rel="alternate"][hreflang="en"]');
      if (links.length > 0) return true;
    }
    return false;
  }

  /* --- Build the popup DOM ------------------------------------------------- */
  function buildPopup(countryCode) {
    var enAvailable = hasEnLocale();
    var countryFR = COUNTRY_NAMES_FR[countryCode] || countryCode;
    var countryEN = COUNTRY_NAMES_EN[countryCode] || countryCode;
    /* Determine which text variant to show */
    var heading, body, primaryLabel, secondaryLabel, primaryAction;

    /* Read translations from DOM meta tags (injected by theme.liquid) or use defaults */
    var meta = document.querySelector('meta[name="c87-country-popup"]');
    var t = {};
    if (meta) {
      try { t = JSON.parse(meta.getAttribute('content')); } catch(e) {}
    }

    var tHeading = t.heading || 'Bienvenue';
    var tBodyFr = t.body_fr || 'Il semble que vous naviguiez depuis {country}. Souhaitez-vous passer en anglais\u00a0?';
    var tBodyEn = t.body_en || 'It looks like you\u2019re visiting from {country}. 87C ships worldwide from France.';
    var tSwitchEn = t.switch_en || 'Switch to English';
    var tStayFr = t.stay_fr || 'Rester en fran\u00e7ais';
    var tContinue = t.continue_shopping || 'Continue shopping';

    if (enAvailable) {
      heading = tHeading;
      body = tBodyFr.replace('{country}', countryFR);
      primaryLabel = tSwitchEn;
      secondaryLabel = tStayFr;
      primaryAction = function () {
        window.location.href = '/en' + window.location.pathname + window.location.search;
      };
    } else {
      heading = tHeading;
      body = tBodyEn.replace('{country}', countryEN);
      primaryLabel = tContinue;
      secondaryLabel = null;
      primaryAction = null;
    }

    /* Create popup element */
    var popup = document.createElement('div');
    popup.className = 'c87-country-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-label', 'Country detection');
    popup.setAttribute('aria-modal', 'false');

    /* Globe icon */
    var iconHTML =
      '<div class="c87-country-popup__icon" aria-hidden="true">' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="12" cy="12" r="10"/>' +
      '<path d="M2 12h20"/>' +
      '<path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>' +
      '</svg></div>';

    /* Close button */
    var closeHTML =
      '<button class="c87-country-popup__close" aria-label="Fermer">' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">' +
      '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>' +
      '</svg></button>';

    /* Buttons */
    var buttonsHTML =
      '<div class="c87-country-popup__actions">' +
      '<button class="c87-country-popup__btn c87-country-popup__btn--primary" data-c87cp-action="primary">' +
      primaryLabel + '</button>';
    if (secondaryLabel) {
      buttonsHTML +=
        '<button class="c87-country-popup__btn c87-country-popup__btn--secondary" data-c87cp-action="secondary">' +
        secondaryLabel + '</button>';
    }
    buttonsHTML += '</div>';

    popup.innerHTML =
      closeHTML + iconHTML +
      '<p class="c87-country-popup__heading">' + heading + '</p>' +
      '<p class="c87-country-popup__body">' + body + '</p>' +
      buttonsHTML;

    return { el: popup, primaryAction: primaryAction };
  }

  /* --- Dismiss helper ------------------------------------------------------ */
  function dismiss(popup) {
    /* Save to localStorage with 30-day TTL */
    try {
      var ttl = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(STORAGE_KEY, String(ttl));
    } catch (e) { /* ignore */ }

    /* Animate out */
    popup.classList.add('c87-country-popup--closing');

    /* Remove after animation (or immediately if reduced motion) */
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) {
      popup.remove();
    } else {
      popup.addEventListener('animationend', function () { popup.remove(); });
      /* Safety: remove after 400ms even if animationend doesn't fire */
      setTimeout(function () { if (popup.parentNode) popup.remove(); }, 400);
    }
  }

  /* --- Focus management ---------------------------------------------------- */
  function trapFocus(popup) {
    var focusable = popup.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    popup.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        dismiss(popup);
        return;
      }
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    /* Focus the primary button after animation settles */
    setTimeout(function () {
      var primary = popup.querySelector('[data-c87cp-action="primary"]');
      if (primary) primary.focus();
    }, 700);
  }

  /* --- Show popup ---------------------------------------------------------- */
  function showPopup(countryCode) {
    var built = buildPopup(countryCode);
    var popup = built.el;
    var primaryAction = built.primaryAction;

    /* Close button */
    var closeBtn = popup.querySelector('.c87-country-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () { dismiss(popup); });
    }

    /* Primary button */
    var primaryBtn = popup.querySelector('[data-c87cp-action="primary"]');
    if (primaryBtn) {
      primaryBtn.addEventListener('click', function () {
        dismiss(popup);
        if (primaryAction) primaryAction();
      });
    }

    /* Secondary button (stay in FR) */
    var secondaryBtn = popup.querySelector('[data-c87cp-action="secondary"]');
    if (secondaryBtn) {
      secondaryBtn.addEventListener('click', function () { dismiss(popup); });
    }

    /* Inject into page */
    document.body.appendChild(popup);

    /* Accessibility: focus trap */
    trapFocus(popup);
  }

  /* --- Main ---------------------------------------------------------------- */
  function init() {
    detectCountry(function (countryCode) {
      /* If France or unknown, don't show */
      if (!countryCode || countryCode === 'FR') return;

      setTimeout(function () { showPopup(countryCode); }, POPUP_DELAY_MS);
    });
  }

  /* Wait for DOM */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
